<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';

// types
import { IPermission, IRole } from '../../../types/user.type';

//composables
import { useMessage } from '../../../composables/useMessage';
import { useToast } from '../../../composables/useToast';

//repositories
import UserApi from '@repositories/user.repository';
import Store from '@store/dataProvider';
import { useI18n } from 'vue-i18n'

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const { messageHTML, showMessage } = useMessage();

const userApiRepository = new UserApi();

// Definir la propiedad 'roleId' que vendrá desde el componente padre
const props = defineProps<{
  roleId: number | null;
}>();

const emit = defineEmits<{
  (e: 'closeModal'): void;
}>();

const permissionData = ref<IPermission[]>([]);
const rolesData = ref<IRole[]>([]);
const roleSelected = ref<IRole>();
const permissionsActives = ref<IPermission[]>([]);
const title = ref<string>('');
const description = ref<string>('');
const loading = ref<boolean>(false);

// Cargar permisos y roles al montar el componente
onMounted(async () => {
  try {
    loading.value = true;
    const { data } = await userApiRepository.getPermissions();
    if (Array.isArray(data)) {
      permissionData.value = data.sort((a, b) =>
        a.descripcion.localeCompare(b.descripcion),
      );
    }

    const dataRoles = await userApiRepository.getRoles();
    rolesData.value = dataRoles;

    // Si el 'roleId' está disponible en las props, seleccionamos el rol automáticamente
    if (props.roleId) {
      handleSelectValue(props.roleId);
    }
  } catch (error) {
    showMessage(t('global.ocurrio_un_error'), 'error');
  } finally {
    loading.value = false;
  }
});

// Observar cambios en el prop 'roleId' para seleccionar el rol cuando cambie
watch(
  () => props.roleId,
  (newRoleId) => {
    if (newRoleId) {
      handleSelectValue(newRoleId);
    }
  },
);

// Función para seleccionar un rol basado en el 'roleId'
const handleSelectValue = async (roleId: number) => {
  const rol = rolesData.value.find((role) => role.id === roleId);

  if (rol) {
    roleSelected.value = rol;

    const { data } = await userApiRepository.getPermissionsByRoleId(rol.id);

    const filterPermissionsActives = permissionData.value.map((item) => {
      const foundItem = data.find((d: IPermission) => d.id === item.id);
      return {
        ...item,
        selected: foundItem ? true : false,
      };
    });

    permissionsActives.value = filterPermissionsActives;
    title.value = rol.title;
    description.value = rol.description;
  }
};

// Función para actualizar el rol
const handleUpdateRol = async () => {
  const selectedPermissions = permissionsActives.value.filter(
    (permission: any) => permission.selected,
  );
  if (!roleSelected.value) return;

  if (!selectedPermissions.some((permission: any) => permission.selected)) {
    showMessage(
	  t('ajustes.usuarios.modalContentRolesUpdate.debes_seleccionar_al'),
      'error',
    );
    return;
  }

  try {
    const { data } = await userApiRepository.updateRole(roleSelected.value.id, {
      permissions: selectedPermissions,
      title: title.value,
      description: description.value,
    });

    let index = store.roles.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
      store.setProp({ key: 'roles', index, value: data });
    }
    $toast?.success(t('ajustes.usuarios.modalContentRolesUpdate.rol_actualizado_con_exito'));
    emit('closeModal');
  } catch (error: any) {
    showMessage(error.message, 'error');
  }
};
</script>

<template>
  <div
    v-if="loading"
    class="d-block position-relative min-h-100"
    style="min-height: 100px"
  >
    <Overlay
      :show="true"
      :circleColor="'#40a4c6'"
      :borderColor="'#40a4c6'"
      class="h-100 w-100"
    />
  </div>
  <div v-else>
    <div class="d-flex">
      <div class="w-100 mx-1">
        <label class="m-1 w-100">{{t('global.nombre')}}</label>
        <input
          :placeholder="t('global.nombre')"
          class="mb-2 form-control input-global"
          v-model="title"
        />
      </div>
      <div class="w-100 mx-1">
        <label class="m-1">{{t('global.descripcion')}}</label>
        <input
          :placeholder="t('global.descripcion')"
          class="mb-2 form-control input-global"
          v-model="description"
        />
      </div>
    </div>
    <!-- Muestra el rol seleccionado si hay uno -->
    <div v-if="roleSelected">
      <p><strong>{{t('ajustes.usuarios.modalContentRolesUpdate.rol_seleccionado')}}</strong> {{ roleSelected.title }}</p>
    </div>

    <!-- Formulario para actualizar el rol -->
    <form @submit.prevent="handleUpdateRol">
      <div>
        <div class="row">
          <div
            v-for="permission in permissionsActives"
            :key="permission.id"
            class="col-12 col-sm-6 col-md-4 col-lg-3 mb-2"
          >
            <div class="d-flex align-items-center p-2 rounded">
              <CheckBox
                :option="false"
                @change="permission.selected = !permission.selected"
                :value="!permission.selected"
              />
              <p class="m-1 mb-0 flex-grow-1 text-truncate">
                {{ permission.descripcion }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="messageHTML" v-html="messageHTML" />
        <CButton class="w-100 fs-6 p-2 mt-2" background="background-blue">
          <template #content>
            <div>
              <span>{{t('global.actualizar')}}</span>
            </div>
          </template>
        </CButton>
      </div>
    </form>
  </div>
</template>
